import type { IReservedBooking } from "@app/booking/types";
import { PaymentIntentCaptureMethod } from "@app/payment/constants/PaymentIntentCaptureMethod";
import { PaymentTypes } from "@app/payment/constants/PaymentTypes";
import { TPaymentIntentModel } from "@app/payment/types";
import React from "react";

import { CheckoutSessionDefaults } from "../constants/CheckoutSessionDefaults";
import { CheckoutSessionStatus } from "../constants/CheckoutSessionStatus";
import { ICheckoutBookingValidationResult } from "../hooks/useCheckoutBookingValidation";
import { ICheckoutSessionState } from "../types";

export interface ICheckoutSessionProviderReviveSessionProps {
    payment_type?: PaymentTypes;
    forceNew?: boolean;
    forceReservedBooking?: Partial<IReservedBooking>;
}

export interface ICheckoutSessionContext {
    state: ICheckoutSessionState;
    setState: (state: Partial<ICheckoutSessionState>) => Promise<void>;
    reviveSession: (props?: ICheckoutSessionProviderReviveSessionProps) => Promise<ICheckoutSessionState>;
    canEnterCheckoutFlow: () => boolean;
    booking?: IReservedBooking;
    paymentIntentModel?: TPaymentIntentModel;
    upsertCheckoutSession: () => Promise<ICheckoutSessionState>;
    clearCheckoutSession: () => Promise<void>;
    hasActiveCheckoutSession: boolean;

    /**
     * Indicated whether the checkout session is initializing.
     * Initializing means that the persisted state is being restored THE FIRST TIME from localStorage or a new session is being created.
     *
     * Persisted state could be restored more than once, for example when the user switches tabs.
     * Therefore we also have the flags isPersisting and isRestoring to indicate whether the state is being persisted or restored.
     */
    isCheckoutSessionInitializing: boolean;

    isLoading: boolean;
    isPaymentConfirming: boolean; // Indicates if the payment confirmation is in progress
    setIsPaymentConfirming: (isActivated: boolean) => void; // Set state when payment confirmation is triggered or stopped
    updateSessionExpiredAt: () => void;
    checkoutBookingValidation?: ICheckoutBookingValidationResult;
    isRestoringPersistedCheckoutSessionState: boolean;
    isPersistingCheckoutSessionState: boolean;
}

export const INITIAL_CHECKOUT_SESSION_CONTEXT_VALUE: ICheckoutSessionContext = {
    state: {
        visitor_id: "",
        booking_hash: null,
        capture_method: PaymentIntentCaptureMethod.AutomaticAsync,
        status: CheckoutSessionStatus.InProgress,
        payment_type: PaymentTypes.Direct,
        expired_at: Date.now() + CheckoutSessionDefaults.SessionTtl,
        has_last_payment_error_been_shown: false,
    },
    setState: () => {
        throw new Error("Not initialized yet");
    },
    reviveSession: () => {
        throw new Error("Not initialized yet");
    },
    isLoading: false,
    hasActiveCheckoutSession: false,
    isCheckoutSessionInitializing: true,
    isPaymentConfirming: false,
    setIsPaymentConfirming: () => undefined,
    canEnterCheckoutFlow: () => false,
    upsertCheckoutSession: () =>
        Promise.resolve({
            session_id: "",
            visitor_id: "",
            booking_hash: null,
            status: CheckoutSessionStatus.InProgress,
            capture_method: PaymentIntentCaptureMethod.AutomaticAsync,
            payment_type: PaymentTypes.Direct,
            has_last_payment_error_been_shown: false,
            expired_at: Date.now() + CheckoutSessionDefaults.SessionTtl,
        }),
    clearCheckoutSession: () => Promise.resolve(),
    updateSessionExpiredAt: () => {
        throw new Error("Not initialized yet");
    },
    isRestoringPersistedCheckoutSessionState: false,
    isPersistingCheckoutSessionState: false,
};

export const CheckoutSessionContext = React.createContext<ICheckoutSessionContext>(INITIAL_CHECKOUT_SESSION_CONTEXT_VALUE);

CheckoutSessionContext.displayName = "CheckoutSessionContext";
